import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { postCoraTurn } from "@/api/cora";
import { CoraAvatar } from "@/components/help/CoraAvatar";
import { CoraOutOfHoursNote, CoraTurnMessage } from "@/components/help/CoraTurnMessage";
import { CORA_UI } from "@/constants/cora";
import { useCoraSettings } from "@/hooks/useCoraSettings";
import type {
  CoraChatEntry,
  CoraConversation,
  CoraHistoryLine,
  CoraTurnResponse,
} from "@/interfaces/cora";

export interface CoraChatViewProps {
  /** Owned by the help panel so it survives navigating away from the chat; null starts fresh. */
  conversation: CoraConversation | null;
  onConversationChange: (
    updater: (prev: CoraConversation | null) => CoraConversation | null,
  ) => void;
  screenKey: string | null;
  focusOnShow: boolean;
  onOpenArticle: (slug: string) => void;
  /** Adds Cora's "what's your question?" line; offered under the greeting below 900px. */
  onTalkToHuman: () => void;
  /** Each change after mount moves focus to "Start a new conversation" (shown once handed off). */
  focusNewConversationTick: number;
}

const NO_ENTRIES: CoraChatEntry[] = [];

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2";

const BUBBLE = "min-w-0 max-w-[86%] px-4 py-3 font-lato text-[#333] text-[14px] leading-[1.5]";
const CORA_BUBBLE = `${BUBBLE} rounded-[4px_16px_16px_16px] bg-light-dust`;
const TYPING_DOT = "size-1.5 rounded-full bg-dark-dust opacity-60 motion-safe:animate-cora-dot";

/** The short text a Cora turn contributes to the history sent with the next turn. */
function turnHistoryText(turn: CoraTurnResponse): string {
  switch (turn.action) {
    case "answer_article":
      return turn.article.passage;
    case "answer_generated":
      return turn.article.answer;
    case "pass_to_team":
      return turn.ticketFiled
        ? CORA_UI.HANDOFF_SENT
        : `${CORA_UI.HANDOFF_NOT_FILED} ${CORA_UI.SUPPORT_EMAIL}.`;
    case "canned":
      return turn.text;
    case "ask_details":
      return turn.questions.map((q) => q.label).join("\n");
  }
}

function buildHistory(entries: CoraChatEntry[]): CoraHistoryLine[] {
  const lines: CoraHistoryLine[] = [];
  for (const entry of entries) {
    const text = (entry.turn ? turnHistoryText(entry.turn) : (entry.text ?? "")).trim();
    // The API rejects empty history lines.
    if (!text) continue;
    lines.push({ role: entry.role, text: text.slice(0, CORA_UI.HISTORY_TEXT_MAX_CHARS) });
  }
  return lines.slice(-CORA_UI.HISTORY_MAX_TURNS);
}

/** "Ask Cora": a chat with the support assistant inside the help panel. */
export function CoraChatView({
  conversation,
  onConversationChange,
  screenKey,
  focusOnShow,
  onOpenArticle,
  onTalkToHuman,
  focusNewConversationTick,
}: CoraChatViewProps) {
  const settings = useCoraSettings();
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const newConversationRef = useRef<HTMLButtonElement>(null);
  // Set when the composer swaps (send settles, a new conversation starts), a human prompt is
  // added, or on a focus-taking mount; consumed after the next commit, once the element that
  // should hold focus is rendered.
  const focusPendingRef = useRef(focusOnShow);
  const conversationIdRef = useRef(conversation?.id ?? null);
  conversationIdRef.current = conversation?.id ?? null;

  const entries = conversation?.entries ?? NO_ENTRIES;
  const handedOff = entries.some((e) => e.turn?.action === "pass_to_team");
  const sendBlocked = pending || !message.trim();
  const latestEntry = entries.at(-1);
  const latestPromptId = latestEntry?.kind === "human-prompt" ? latestEntry.id : null;
  // A prompt already present at mount is not "new": mount focus follows focusOnShow.
  const seenPromptIdRef = useRef(latestPromptId);
  const seenFocusTickRef = useRef(focusNewConversationTick);
  const seeds = (screenKey ? settings.seeds[screenKey] : undefined) ?? settings.seeds.default ?? [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: `entries` and `pending` are the triggers — each new entry or the typing bubble scrolls into view.
  useEffect(() => {
    const log = logRef.current;
    log?.scrollTo({ top: log.scrollHeight });
  }, [entries, pending]);

  // Declared before the focus effect so a prompt added while the chat is shown focuses the
  // composer in the same commit (the link that added it may just have unmounted).
  useEffect(() => {
    if (latestPromptId && latestPromptId !== seenPromptIdRef.current) {
      seenPromptIdRef.current = latestPromptId;
      focusPendingRef.current = true;
    }
  }, [latestPromptId]);

  useEffect(() => {
    if (focusNewConversationTick === seenFocusTickRef.current) return;
    seenFocusTickRef.current = focusNewConversationTick;
    focusPendingRef.current = true;
  }, [focusNewConversationTick]);

  useEffect(() => {
    if (!focusPendingRef.current) return;
    focusPendingRef.current = false;
    if (handedOff) newConversationRef.current?.focus();
    else textareaRef.current?.focus();
  });

  async function send(text: string) {
    const outgoing = text.trim();
    if (!outgoing || pending) return;
    const sentId = conversation?.id ?? crypto.randomUUID();
    const requestHuman = conversation?.handoffRequested ?? false;
    const userEntry: CoraChatEntry = { id: crypto.randomUUID(), role: "user", text: outgoing };
    const history = buildHistory(entries);
    onConversationChange((prev) => {
      if (prev === null) return { id: sentId, entries: [userEntry], handoffRequested: false };
      return prev.id === sentId ? { ...prev, entries: [...prev.entries, userEntry] } : prev;
    });
    setMessage("");
    setError(null);
    setPending(true);
    try {
      // Promise.all rejects as soon as the request does, so a failure skips the typing minimum.
      const [turn] = await Promise.all([
        postCoraTurn({
          conversationId: sentId,
          message: outgoing,
          screen: screenKey,
          history,
          requestHuman,
        }),
        new Promise<void>((resolve) => setTimeout(resolve, CORA_UI.MIN_TYPING_MS)),
      ]);
      const coraEntry: CoraChatEntry = { id: crypto.randomUUID(), role: "cora", turn };
      // Only a request that went to the team clears the flag; one set while this send was in
      // flight still applies to the next message.
      onConversationChange((prev) =>
        prev?.id === sentId
          ? {
              ...prev,
              entries: [...prev.entries, coraEntry],
              handoffRequested: requestHuman ? false : prev.handoffRequested,
            }
          : prev,
      );
    } catch (err) {
      console.error("CoraChatView.send", (err as { message?: string } | undefined)?.message);
      onConversationChange((prev) =>
        prev?.id === sentId
          ? { ...prev, entries: prev.entries.filter((e) => e.id !== userEntry.id) }
          : prev,
      );
      if (conversationIdRef.current === sentId) {
        setMessage(outgoing);
        setError(CORA_UI.UNAVAILABLE);
      }
    } finally {
      setPending(false);
      focusPendingRef.current = true;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void send(message);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send(message);
    }
  }

  // The clicked chip unmounts once its question is in the log, so focus moves to the composer.
  function sendSeed(seed: string) {
    if (pending) return;
    focusPendingRef.current = true;
    void send(seed);
  }

  function markTicketSent(conversationId: string, entryId: string) {
    onConversationChange((prev) =>
      prev?.id === conversationId
        ? {
            ...prev,
            entries: prev.entries.map((e) => (e.id === entryId ? { ...e, ticketSent: true } : e)),
          }
        : prev,
    );
  }

  function startNewConversation() {
    onConversationChange(() => ({
      id: crypto.randomUUID(),
      entries: [],
      handoffRequested: false,
    }));
    setMessage("");
    setError(null);
    focusPendingRef.current = true;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={logRef}
        className="min-h-0 flex-1 overflow-y-auto px-10 motion-safe:scroll-smooth pt-[30px] pb-6 [overscroll-behavior:contain] max-[900px]:px-5 max-[900px]:pt-6"
      >
        <div className="mx-auto flex w-full max-w-[560px] flex-col max-[900px]:max-w-none">
          {entries.length === 0 && (
            <div className="flex flex-col items-center pt-3.5 text-center">
              <CoraAvatar size={76} />
              <h2 className="m-0 mt-3 font-crimson font-normal text-[24px] text-rodeo-black">
                {settings.greetingTitle}
              </h2>
              <p className="m-0 mt-2 max-w-[44ch] font-lato text-[14px] text-graphite leading-relaxed">
                {settings.greetingBody}
              </p>
              {seeds.length > 0 && (
                <ul className="m-0 mt-5 flex list-none flex-col items-center gap-2 p-0">
                  {seeds.map((seed) => (
                    <li key={seed}>
                      {/* aria-disabled, not disabled: disabling the clicked chip would drop focus
                          to <body>. sendSeed() already ignores pending clicks. */}
                      <button
                        type="button"
                        onClick={() => sendSeed(seed)}
                        aria-disabled={pending}
                        className={`cursor-pointer rounded-[20px] border border-smoke bg-white px-4 py-2.5 font-lato font-semibold text-[12px] text-rodeo-black transition-colors hover:border-dark-dust hover:bg-clear-dust aria-disabled:cursor-default aria-disabled:opacity-35 ${FOCUS_RING}`}
                      >
                        {seed}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={onTalkToHuman}
                className={`mt-5 flex cursor-pointer items-center gap-1.5 rounded border-0 bg-transparent px-1 py-0.5 font-bold font-lato text-[13px] text-dust-ink transition-colors hover:text-rodeo-black min-[901px]:hidden ${FOCUS_RING}`}
              >
                <SupportAgentIcon aria-hidden="true" sx={{ fontSize: 17 }} />
                {CORA_UI.TALK_TO_HUMAN}
              </button>
            </div>
          )}

          <div className="flex flex-col gap-4" aria-live="polite">
            {entries.map((entry, index) =>
              entry.role === "user" ? (
                <p
                  key={entry.id}
                  className={`${BUBBLE} m-0 self-end motion-safe:animate-cora-rise whitespace-pre-line rounded-[16px_4px_16px_16px] border border-smoke bg-white`}
                >
                  {entry.text}
                </p>
              ) : (
                <div
                  key={entry.id}
                  className="flex items-start gap-2.5 motion-safe:animate-cora-rise"
                >
                  <span className="mt-0.5 shrink-0">
                    <CoraAvatar size={28} />
                  </span>
                  <div
                    className={`${CORA_BUBBLE} ${
                      entry.turn?.action === "ask_details" ? "flex-1" : ""
                    }`}
                  >
                    {entry.turn && conversation ? (
                      <CoraTurnMessage
                        turn={entry.turn}
                        onOpenArticle={onOpenArticle}
                        conversationId={conversation.id}
                        history={buildHistory(entries.slice(0, index))}
                        screenKey={screenKey}
                        ticketSent={entry.ticketSent}
                        onTicketSent={() => markTicketSent(conversation.id, entry.id)}
                      />
                    ) : (
                      <div className="flex flex-col gap-3">
                        <p className="m-0 whitespace-pre-line">{entry.text}</p>
                        {entry.kind === "human-prompt" && !settings.teamOnline && (
                          <CoraOutOfHoursNote supportHours={settings.supportHours} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ),
            )}
            {pending && (
              <div className="flex items-start gap-2.5 motion-safe:animate-cora-rise">
                <span className="mt-0.5 shrink-0">
                  <CoraAvatar size={28} />
                </span>
                <div className="flex items-center gap-[5px] rounded-[4px_16px_16px_16px] bg-light-dust px-4 py-[17px]">
                  <span className="sr-only">{CORA_UI.TYPING_LABEL}</span>
                  <span aria-hidden="true" className={TYPING_DOT} />
                  <span aria-hidden="true" className={`${TYPING_DOT} [animation-delay:0.28s]`} />
                  <span aria-hidden="true" className={`${TYPING_DOT} [animation-delay:0.56s]`} />
                </div>
              </div>
            )}
            {error && (
              <p role="alert" className="m-0 font-lato text-[13px] text-good-wine">
                {error}
              </p>
            )}
          </div>

          {/* No composer after a handoff: handoffs are deduped per conversation, so another
              message here would never reach the team. */}
          {handedOff && (
            <button
              ref={newConversationRef}
              type="button"
              onClick={startNewConversation}
              className={`mt-3 ml-[38px] cursor-pointer self-start rounded border-0 bg-transparent p-0 font-bold font-lato text-[13px] text-dust-ink underline-offset-2 transition-colors hover:text-rodeo-black hover:underline ${FOCUS_RING}`}
            >
              {CORA_UI.NEW_CONVERSATION}
            </button>
          )}
        </div>
      </div>

      {!handedOff && (
        <div className="flex-none px-10 pt-0 pb-7 max-[900px]:px-5 max-[900px]:pb-5">
          <form onSubmit={handleSubmit} className="mx-auto w-full max-w-[560px]">
            <div className="flex items-end gap-3 rounded-[10px] border border-smoke bg-white py-3 pr-3 pl-4 transition-colors focus-within:border-dark-dust">
              {/* readOnly, not disabled: disabling the focused textarea would drop focus to <body>. */}
              <textarea
                ref={textareaRef}
                aria-label={CORA_UI.COMPOSER_LABEL}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={CORA_UI.COMPOSER_PLACEHOLDER}
                maxLength={CORA_UI.MESSAGE_MAX_CHARS}
                readOnly={pending}
                aria-busy={pending}
                rows={2}
                className="block h-11 min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent p-0 font-lato text-[#333] text-[14px] leading-[1.55] outline-none placeholder:text-graphite"
              />
              {/* aria-disabled, not disabled: a mouse-clicked Send holds focus, and disabling it
                  would drop focus to <body>. send() already ignores empty or pending submits. */}
              <button
                type="submit"
                aria-label={CORA_UI.SEND_LABEL}
                aria-disabled={sendBlocked}
                className={`flex size-9 shrink-0 items-center justify-center rounded-full border-0 bg-[#333] text-white transition-opacity ${
                  sendBlocked ? "cursor-default opacity-35" : "cursor-pointer hover:bg-[#1a1a1a]"
                } ${FOCUS_RING}`}
              >
                <ArrowUpwardIcon aria-hidden="true" sx={{ fontSize: 18 }} />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default CoraChatView;
